import { Lang, TranslateEnumFunction } from 'language/translator'
import { ECurrency, KPI_UNIT } from 'types/Enums'
import type { TAttributeOptionDto } from '@homepal/api-contract'
import { TKeyMetricDataBody } from 'types/GlobalKeyMetric'
import {
  IMetric,
  isCustomEconomyMetric,
  ISelectMetric,
  TMetricDataPostBody,
  TMetricQueryParams,
  TMetricSettings
} from 'types/GlobalMetric'
import { PeriodFilter } from 'types/GlobalWidget'
import { getSlicesKey } from 'utils/slices'
import { parseLabel } from './widgetData'
import { getTheme } from 'themes'

// the display order of the variant subcategories: outcome first,
// then budget and the forecasts
export const VARIANT_SUBCATEGORY_ORDER = [
  'result',
  'budget',
  'forecast-1',
  'forecast-2',
  'forecast-3'
]

export function resolveUnit(
  unit?: string | null,
  currency?: string | null
): string | null {
  if (!unit) return null
  if (!currency || currency === ECurrency.SEK) return unit

  // another currency is written by its code: tkr becomes t EUR
  return unit.replace(
    new RegExp(`^(t|m|md)?${KPI_UNIT.SEK}`),
    (_, prefix?: string) => (prefix ? `${prefix} ${currency}` : currency)
  )
}

export function getMetricDefaultSettings(
  metric: IMetric | undefined,
  variantId?: string | null
) {
  if (!metric) return undefined
  if (isCustomEconomyMetric(metric)) {
    return metric.variants.find((vr) => vr.id === variantId)?.default_settings
  }

  return metric.default_settings
}

function buildFilterKey(filter?: TMetricQueryParams['filter']): string {
  if (!filter || Object.keys(filter).length === 0) return 'NO_QUERY_FILTER'

  return Object.keys(filter)
    .sort()
    .map((key) => `${key}=${[...filter[key].eq].sort().join(',')}`)
    .join('|')
}

function buildRealEstatesKey(realEstates?: string[]): string {
  if (!realEstates || realEstates.length === 0) return 'NO_REAL_ESTATES'

  return [...realEstates].sort().join(',')
}

export function getMetricDataKey(body?: TMetricDataPostBody): string {
  if (!body)
    return [
      'NO_SLICE',
      'NO_DIST',
      'NO_ROLL',
      'NO_VARIANT',
      'NO_PERIOD',
      'NO_SEGMENT',
      'NO_FROM_DATE',
      'NO_TO_DATE',
      'NO_QUERY_FILTER',
      'NO_REAL_ESTATES',
      'NO_SOURCE',
      'KEYFIG'
    ].join('|')

  const sliceKey = getSlicesKey(body.slices)

  return [
    sliceKey,
    body.distribution ?? 'NO_DIST',
    body.rolling_sum ?? 'NO_ROLL',
    body.variant_kpi_template_id ?? 'NO_VARIANT',
    body.query_params?.period_filter ?? 'NO_PERIOD',
    body.query_params?.segment_by ?? 'NO_SEGMENT',
    body.query_params?.widget_type ?? 'NO_WIDGET_TYPE',
    body.query_params?.from_date ?? 'NO_FROM_DATE',
    body.query_params?.to_date ?? 'NO_TO_DATE',
    buildFilterKey(body.query_params?.filter),
    buildRealEstatesKey(body.query_params?.real_estates),
    body.source?.id ?? 'NO_SOURCE',
    body.include_key_figure === false ? 'NO_KEYFIG' : 'KEYFIG'
  ].join('|')
}

export function getKeyMetricDataKey(body?: TKeyMetricDataBody): string {
  return [
    body?.query_params?.period_filter ?? PeriodFilter.LAST_12_MONTHS_TO_TODAY,
    body?.query_params?.segment_by ?? 'NO_SEGMENT',
    body?.query_params?.from_date ?? 'NO_FROM',
    body?.query_params?.to_date ?? 'NO_TO',
    buildFilterKey(body?.query_params?.filter)
  ].join('|')
}

type RawDataLike = {
  data: { segment: string | null; value: number | null }[]
}

export const getLabels = (params: {
  lang: Lang
  rawData: RawDataLike | null
  segmentOption: TAttributeOptionDto | null
}) => {
  const { lang, rawData, segmentOption } = params
  const segmentTechnicalName = segmentOption?.technical_name || 'year_month'
  const segmentType = segmentOption?.type || 'DATE'

  if (rawData === null) return []

  return rawData.data.map((item) =>
    parseLabel(
      {
        label: item.segment,
        technical_name: segmentTechnicalName,
        type: segmentType
      },
      lang,
      true
    )
  )
}

export const getValues = (
  params: { rawData: RawDataLike | null },
  rawDataError?: boolean
) => {
  const { rawData } = params

  if (rawData === null || rawDataError) return []

  return rawData.data.map((item) => item.value)
}

export const getLineColor = (params: {
  increaseIsPositive: boolean
  values: (number | null)[]
}): string => {
  const { increaseIsPositive, values } = params

  const theme = getTheme('light')
  const error = theme.palette.error.text as string
  const success = theme.palette.success.text as string

  const diff = (values[values.length - 1] ?? 0) - (values[0] ?? 0) // @TODO

  if (diff < 0) {
    return increaseIsPositive ? error : success
  } else {
    // diff > 0
    return increaseIsPositive ? success : error
  }
}

export const getIncreaseIsPositive = (params: {
  metric: ISelectMetric | null
}) => {
  const { metric } = params

  if (!metric) return true

  return metric.increase_is_positive
}

export function getMetricTitle(
  metric: ISelectMetric,
  settings: TMetricSettings,
  lang: Lang,
  translateEnum: TranslateEnumFunction
): string {
  const title = isCustomEconomyMetric(metric)
    ? metric.full_title[lang]
    : metric.title[lang]

  const slices = settings.slices
    .map((sliceId) => {
      const slice = metric.slices.all.find((item) => item.id === sliceId)

      if (!slice) return null

      return slice.name
    })
    .filter(Boolean)
    .join(' & ')
  const slicePart = slices ? ` > ${slices}` : ''

  const distribution = settings.distribution
    ? translateEnum('show_pers', settings.distribution).abbreviation
    : null
  const distributionPart = distribution ? ` / ${distribution}` : ''

  const rollingSum = settings.rolling_sum
    ? translateEnum('rolling_types', settings.rolling_sum).abbreviation
    : null
  const rollingSumPart = rollingSum ? ` (${rollingSum})` : ''

  return title + slicePart + distributionPart + rollingSumPart
}

export function getMetricLinkSearch(
  settings: TMetricSettings
): string | undefined {
  const params = new URLSearchParams()

  if (settings.distribution) {
    params.set('distribution', settings.distribution)
  }
  if (settings.rolling_sum) {
    params.set('rolling_sum', settings.rolling_sum)
  }
  if (settings.slices.length > 0) {
    params.set('slices', settings.slices.join(','))
  }
  if (settings.variant_kpi_template_id) {
    params.set('variant', settings.variant_kpi_template_id)
  }

  const qs = params.toString()
  const url = qs ? `?${qs}` : undefined

  return url
}
